import React, { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import axios from "axios"
import {
  ArrowLeft,
  BriefcaseBusiness,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Clock3,
  Send,
  Server,
  ShieldCheck,
  UserRound,
  UsersRound,
} from "lucide-react"
import "./UserDetails.css"

const EMPTY = "—"
const PAGE_SIZE = 6

const firstValue = (...values) =>
  values.find(
    value =>
      value !== null && value !== undefined && String(value).trim() !== ""
  )
const display = value => {
  const resolved = firstValue(value)
  if (resolved === undefined) return EMPTY
  if (typeof resolved === "object")
    return (
      firstValue(
        fullName(resolved),
        resolved.label,
        resolved.title,
        resolved.company_name,
        resolved.job_title
      ) || EMPTY
    )
  if (typeof resolved === "boolean") return resolved ? "Yes" : "No"
  return String(resolved)
}
const asArray = value => (Array.isArray(value) ? value : [])

const formatDate = value => {
  if (!value) return EMPTY
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString([], { dateStyle: "medium", timeStyle: "short" })
}

const fullName = value => {
  if (!value) return ""
  if (typeof value === "string") return value.trim()
  return [
    value.first_name,
    value.middle_name,
    value.last_name,
    value.name,
    value.full_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim()
}

const nameFrom = (row, keys = []) => {
  for (const key of keys) {
    const value = row?.[key]
    const name = fullName(value)
    if (name) return name
  }
  return EMPTY
}

const textFrom = (row, keys = []) =>
  display(keys.map(key => row?.[key]).find(value => value !== undefined))

const unwrap = payload => {
  let value = payload?.data ?? payload ?? {}
  if (
    value?.data &&
    typeof value.data === "object" &&
    !Array.isArray(value.data)
  )
    value = value.data
  return value || {}
}

const collection = (payload, keys) => {
  for (const key of keys) {
    const value = payload?.[key]
    if (Array.isArray(value)) return value
    if (Array.isArray(value?.results)) return value.results
    if (Array.isArray(value?.data)) return value.data
    if (Array.isArray(value?.items)) return value.items
  }
  return []
}

const profileFrom = payload =>
  payload?.profile ||
  payload?.user ||
  payload?.user_details ||
  payload?.user_data ||
  payload ||
  {}

const SectionTable = ({
  title,
  description,
  icon: Icon,
  rows,
  columns,
  loading,
  emptyMessage,
}) => {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize))
  const currentPage = Math.min(page, totalPages)
  const visibleRows = rows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  )

  useEffect(() => setPage(1), [rows.length, pageSize])

  return (
    <section className="user-details-section">
      <div className="user-details-section-heading">
        <div className="user-details-section-icon">
          <Icon size={18} aria-hidden="true" />
        </div>
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <span className="user-details-section-count">{rows.length}</span>
      </div>
      {loading ? (
        <div className="user-details-state" role="status">
          <span className="user-details-spinner" />
          Loading {title.toLowerCase()}...
        </div>
      ) : rows.length === 0 ? (
        <div className="user-details-state user-details-empty">
          <CircleAlert size={18} aria-hidden="true" />
          <span>{emptyMessage}</span>
        </div>
      ) : (
        <>
          <div className="user-details-table-wrap">
            <table className="user-details-table">
              <thead>
                <tr>
                  {columns.map(column => (
                    <th key={column.label}>{column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row, index) => (
                  <tr
                    key={
                      row.id ||
                      row.user_id ||
                      row.job_id ||
                      row.job_candidate_id ||
                      `${currentPage}-${index}`
                    }
                  >
                    {columns.map(column => (
                      <td key={column.label}>
                        {column.render
                          ? column.render(row)
                          : display(row[column.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="user-details-pagination">
            <span>
              {(currentPage - 1) * pageSize + 1}–
              {Math.min(currentPage * pageSize, rows.length)} of {rows.length}
            </span>
            <label>
              Rows{" "}
              <select
                value={pageSize}
                onChange={event => setPageSize(Number(event.target.value))}
              >
                <option value={6}>6</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
            </label>
            <button
              type="button"
              aria-label={`Previous ${title} page`}
              disabled={currentPage === 1}
              onClick={() => setPage(currentPage - 1)}
            >
              <ChevronLeft size={16} />
            </button>
            <strong>
              {currentPage} / {totalPages}
            </strong>
            <button
              type="button"
              aria-label={`Next ${title} page`}
              disabled={currentPage === totalPages}
              onClick={() => setPage(currentPage + 1)}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </>
      )}
    </section>
  )
}

const valueCell =
  (keys, formatter = display) =>
  row =>
    formatter(keys.map(key => row?.[key]).find(value => value !== undefined))

export default function UserDetails() {
  const { userId } = useParams()
  const navigate = useNavigate()
  const [payload, setPayload] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    let active = true
    if (!userId) {
      try {
        const storedUser = JSON.parse(
          sessionStorage.getItem("user-info") || "null"
        )
        const loggedInUserId = storedUser?.user_details?.user_id
        if (loggedInUserId) {
          navigate(`/user-details/${loggedInUserId}`, { replace: true })
          return () => {
            active = false
          }
        }
      } catch (storageError) {
        // Keep the empty state when session data cannot be parsed.
      }
    }
    const loadDetails = async () => {
      setLoading(true)
      setError("")
      setPayload(null)
      try {
        const response = await axios.get(
          `/api/v1/users/${encodeURIComponent(userId)}/ats-details/`
        )
        if (active) setPayload(unwrap(response.data))
      } catch (requestError) {
        if (active)
          setError(
            requestError?.response?.data?.message ||
              "Unable to load user details."
          )
      } finally {
        if (active) setLoading(false)
      }
    }
    if (userId) loadDetails()
    else {
      setLoading(false)
      setError("A user was not selected.")
    }
    return () => {
      active = false
    }
  }, [userId])

  const profile = profileFrom(payload)
  const jobs = useMemo(
    () => collection(payload, ["jobs_created", "jobs", "created_jobs"]),
    [payload]
  )
  const candidates = useMemo(
    () =>
      collection(payload, [
        "candidates_added",
        "candidates",
        "added_candidates",
      ]),
    [payload]
  )
  const interviews = useMemo(
    () =>
      collection(payload, [
        "interviews_scheduled",
        "interviews",
        "scheduled_interviews",
      ]),
    [payload]
  )
  const submissions = useMemo(
    () =>
      collection(payload, [
        "hrms_submissions",
        "hrms",
        "submissions",
        "hrms_candidates",
      ]),
    [payload]
  )
  const smtpAccounts = useMemo(
    () => collection(payload, ["smtp_accounts", "smtp", "smtpAccounts"]),
    [payload]
  )
  const profileName =
    nameFrom(profile, ["name", "full_name", "user_name", "employee_name"]) ||
    firstValue(fullName(profile), `User ${userId}`)

  const jobColumns = [
    {
      label: "Job title",
      render: row =>
        textFrom(row, [
          "job_title",
          "title",
          "job_name",
          "requirement",
          "name",
        ]),
    },
    {
      label: "Company",
      render: row => textFrom(row, ["company_name", "company"]),
    },
    {
      label: "Status",
      render: row => textFrom(row, ["status", "job_status_name", "job_status"]),
    },
    {
      label: "Created",
      render: row =>
        valueCell(
          ["created_at", "created_date", "date_created"],
          formatDate
        )(row),
    },
  ]
  const candidateColumns = [
    {
      label: "Candidate",
      render: row => nameFrom(row, ["candidate_name", "name", "candidate"]),
    },
    {
      label: "Email",
      render: row =>
        textFrom(row, ["email", "candidate_email", "personal_email"]),
    },
    {
      label: "Job",
      render: row => textFrom(row, ["job_title", "job_name", "requirement"]),
    },
    {
      label: "Added",
      render: row =>
        valueCell(["added_at", "created_at", "applied_date"], formatDate)(row),
    },
  ]
  const interviewColumns = [
    {
      label: "Candidate",
      render: row => nameFrom(row, ["candidate_name", "name", "candidate"]),
    },
    {
      label: "Job",
      render: row => textFrom(row, ["job_title", "job_name", "requirement"]),
    },
    {
      label: "Interview",
      render: row =>
        valueCell(["scheduled_at", "interview_date", "date"], formatDate)(row),
    },
    {
      label: "Status",
      render: row => textFrom(row, ["status", "interview_status"]),
    },
  ]
  const submissionColumns = [
    {
      label: "Candidate",
      render: row => nameFrom(row, ["candidate_name", "name", "candidate"]),
    },
    {
      label: "Job",
      render: row => textFrom(row, ["job_title", "job_name", "requirement"]),
    },
    {
      label: "Company",
      render: row => textFrom(row, ["company_name", "company"]),
    },
    {
      label: "Status",
      render: row =>
        textFrom(row, ["status", "submission_status", "hrms_status"]),
    },
  ]
  const smtpColumns = [
    {
      label: "SMTP host",
      render: row => textFrom(row, ["host", "smtp_host", "server"]),
    },
    {
      label: "Username",
      render: row => textFrom(row, ["username", "user_name", "email"]),
    },
    {
      label: "From email",
      render: row => textFrom(row, ["from_email", "email_address", "email"]),
    },
    {
      label: "Status",
      render: row =>
        row?.is_active === undefined
          ? textFrom(row, ["status", "account_status"])
          : row.is_active
          ? "Active"
          : "Inactive",
    },
  ]

  const profileFields = [
    ["Email", firstValue(profile.email, profile.email_address)],
    ["Phone", firstValue(profile.phone, profile.phone_number, profile.mobile)],
    ["Role", firstValue(profile.role_name, profile.role, profile.user_role)],
    ["Department", firstValue(profile.department_name, profile.department)],
    ["Designation", firstValue(profile.designation_name, profile.designation)],
    ["Company", firstValue(profile.company_name, profile.company)],
    ["Joined", formatDate(firstValue(profile.date_joined, profile.joined_at))],
    [
      "Status",
      firstValue(
        profile.status,
        profile.is_active === undefined
          ? undefined
          : profile.is_active
          ? "Active"
          : "Inactive"
      ),
    ],
  ]

  return (
    <main className="user-details-page">
      <div className="user-details-topbar">
        <button
          type="button"
          className="user-details-back"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={17} />
          Back to users
        </button>
        <span className="user-details-breadcrumb">User details</span>
      </div>
      <header className="user-details-hero">
        <div className="user-details-avatar">
          <UserRound size={30} />
        </div>
        <div className="user-details-hero-copy">
          <span className="user-details-eyebrow">ATS account</span>
          <h1>{profileName}</h1>
          <p>
            {display(firstValue(profile.email, profile.email_address))}{" "}
            <span>•</span> User ID {userId}
          </p>
        </div>
        <div className="user-details-hero-stat">
          <strong>{jobs.length + candidates.length + interviews.length}</strong>
          <span>tracked activities</span>
        </div>
      </header>
      {error && (
        <div className="user-details-error" role="alert">
          <CircleAlert size={18} />
          {error}
        </div>
      )}
      <section className="user-details-section user-details-profile-section">
        <div className="user-details-section-heading">
          <div className="user-details-section-icon">
            <ShieldCheck size={18} />
          </div>
          <div>
            <h2>Profile</h2>
            <p>Account and organization information</p>
          </div>
        </div>
        {loading ? (
          <div className="user-details-state" role="status">
            <span className="user-details-spinner" />
            Loading profile...
          </div>
        ) : (
          <div className="user-details-profile-grid">
            {profileFields.map(([label, value]) => (
              <div className="user-details-field" key={label}>
                <span>{label}</span>
                <strong>{display(value)}</strong>
              </div>
            ))}
          </div>
        )}
      </section>
      <div className="user-details-grid">
        <SectionTable
          title="Jobs Created"
          description="Jobs created by this user"
          icon={BriefcaseBusiness}
          rows={jobs}
          columns={jobColumns}
          loading={loading}
          emptyMessage="No jobs created by this user."
        />
        <SectionTable
          title="Candidates Added"
          description="Candidates added to the ATS"
          icon={UsersRound}
          rows={candidates}
          columns={candidateColumns}
          loading={loading}
          emptyMessage="No candidates added by this user."
        />
        <SectionTable
          title="Interviews Scheduled"
          description="Interview activity assigned to this user"
          icon={CalendarDays}
          rows={interviews}
          columns={interviewColumns}
          loading={loading}
          emptyMessage="No interviews scheduled by this user."
        />
        <SectionTable
          title="HRMS Submissions"
          description="Candidates submitted to HRMS"
          icon={Send}
          rows={submissions}
          columns={submissionColumns}
          loading={loading}
          emptyMessage="No HRMS submissions found."
        />
        <SectionTable
          title="SMTP Accounts"
          description="Configured sending accounts"
          icon={Server}
          rows={smtpAccounts}
          columns={smtpColumns}
          loading={loading}
          emptyMessage="No SMTP accounts found."
        />
      </div>
      {!loading && !error && (
        <div className="user-details-footer-note">
          <Clock3 size={15} />
          Sensitive credentials and secret fields are intentionally hidden.
        </div>
      )}
    </main>
  )
}
